import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { BarSeriesOption, EChartsOption } from 'echarts';
import {
  ChartComponentProps,
  getBaseOption,
  getChartNumericFields,
  getDisplayName,
  buildCategoryAxis,
  buildValueAxis,
  getCartesianChartGrid,
  shouldShowBarValueLabels,
  formatNumberShort,
  formatTooltipValue,
  getChartValueStats,
  getHorizontalValueAxisScale,
  HORIZONTAL_VALUE_LABEL_RIGHT_RESERVE,
  formatTooltipStatsLine,
  ECHARTS_OPTS,
  CHART_LABEL_FONT_SIZE,
  CHART_TEXT_COLOR,
} from './chartConfig';

const BAR_VALUE_LABEL_DISTANCE = 12;

export const BarChart: React.FC<ChartComponentProps> = ({ chart, height = 320, colors, presentation }) => {
  const option = useMemo((): EChartsOption => {
    const data = chart.data || [];
    if (data.length === 0) return {};

    const fields = getChartNumericFields(chart, data);
    const base = getBaseOption(colors);

    const isHorizontal = chart.type === 'horizontal_bar' || chart.type === 'diverging_bar';
    const isStacked = chart.type === 'stacked_bar';
    const isDiverging = chart.type === 'diverging_bar';
    const showValueLabels = presentation?.showValueLabels ?? shouldShowBarValueLabels(chart, data, fields);
    const gridLayout = getCartesianChartGrid(chart, data, fields);
    // Range slider is intentionally disabled for now.
    // Keep the original condition here so it can be restored later if needed.
    // const showRangeSlider = !isHorizontal && !!presentation?.enableRangeSlider && data.length > 24;

    if (!isHorizontal) {
      base.grid = {
        ...(base.grid as Record<string, unknown>),
        top: gridLayout.top,
        bottom: gridLayout.bottom,
      };
    }

    // Preserve the extra bottom grid spacing required by the range slider, but do not apply it for now.
    // if (showRangeSlider) {
    //   base.grid = {
    //     ...(base.grid as Record<string, unknown>),
    //     bottom: 96,
    //   };
    // }

    const multiBar = fields.length > 1 && !isStacked;
    const series: BarSeriesOption[] = fields.map((field): BarSeriesOption => {
      const barSeries: BarSeriesOption = {
        name: getDisplayName(chart, field),
        type: 'bar',
        data: data.map((d) => {
          const rawValue = Number(d[field] ?? d.value ?? 0);
          const value = Number.isFinite(rawValue) ? rawValue : 0;

          if (!isHorizontal || !showValueLabels) return value;

          return {
            value,
            label: {
              position: value < 0 ? 'left' : 'right',
              align: value < 0 ? 'right' : 'left',
            },
          };
        }),
        barMaxWidth: multiBar ? 28 : 48,
        itemStyle: {
          borderRadius: isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0],
        },
        label: {
          show: showValueLabels,
          position: isHorizontal ? 'right' : 'top',
          rotate: 0,
          distance: BAR_VALUE_LABEL_DISTANCE,
          align: isHorizontal ? 'left' : 'center',
          verticalAlign: isHorizontal ? 'middle' : 'bottom',
          fontSize: CHART_LABEL_FONT_SIZE,
          color: CHART_TEXT_COLOR,
          formatter: (params) => {
            const value = Number(Array.isArray(params.value) ? params.value[0] : params.value ?? 0);
            return value === 0 ? '' : formatNumberShort(value);
          },
        },
        // Repo-2 branding pass: value labels should stay readable without covering the bars.
        labelLayout: {
          hideOverlap: true,
          moveOverlap: isHorizontal ? 'shiftY' : undefined,
        },
      };

      if (isStacked) {
        barSeries.stack = 'total';
      }

      return barSeries;
    });

    const categoryAxis = { ...(buildCategoryAxis(chart, data) as Record<string, unknown>) };
    const valueAxis = { ...(buildValueAxis(chart, fields) as Record<string, unknown>) };
    const horizontalAxisScale = isHorizontal
      ? getHorizontalValueAxisScale(data, fields, showValueLabels)
      : null;

    if (horizontalAxisScale) {
      valueAxis.min = horizontalAxisScale.min;
      valueAxis.max = horizontalAxisScale.max;
    }

    if (isHorizontal && showValueLabels) {
      // Repo-2 branding pass: horizontal bars need reserved label space so values never sit on the bar edge.
      base.grid = {
        ...(base.grid as Record<string, unknown>),
        right: HORIZONTAL_VALUE_LABEL_RIGHT_RESERVE,
      };
    }

    if (presentation?.gridOverrides) {
      base.grid = {
        ...(base.grid as Record<string, unknown>),
        ...(presentation.gridOverrides as Record<string, unknown>),
      };
    }

    if (isHorizontal) {
      base.grid = {
        ...(base.grid as Record<string, unknown>),
        containLabel: false,
      };
      const gridLeft = Number((base.grid as Record<string, unknown>)?.left ?? 0);
      const categoryLabelWidth = Math.max(96, gridLeft - 28);
      const truncateAt = Math.max(12, Math.floor(categoryLabelWidth / 6.4));
      categoryAxis.axisLabel = {
        ...(categoryAxis.axisLabel as Record<string, unknown>),
        rotate: 0,
        hideOverlap: false,
        interval: 0,
        margin: 8,
        width: categoryLabelWidth,
        overflow: 'truncate',
        formatter: (value: string) => (
          value && value.length > truncateAt
            ? `${value.slice(0, Math.max(1, truncateAt - 3))}...`
            : value
        ),
      };
      // ECharts renders category y-axes bottom-to-top by default. For
      // horizontal ranking bars, keep the first sorted row visually at
      // the top so "High to low" and backend Top-N ordering read
      // naturally from top to bottom.
      categoryAxis.inverse = true;
      if (presentation?.hideYAxisName) {
        categoryAxis.name = '';
      }
      if (presentation?.hideXAxisLabels) {
        valueAxis.axisLabel = { ...(valueAxis.axisLabel as Record<string, unknown>), show: false };
        valueAxis.axisTick = { ...(valueAxis.axisTick as Record<string, unknown>), show: false };
        valueAxis.name = presentation.hideXAxisName ? '' : valueAxis.name;
      }
      if (presentation?.hideYAxisLabels) {
        categoryAxis.axisLabel = { ...(categoryAxis.axisLabel as Record<string, unknown>), show: false };
        categoryAxis.axisTick = { ...(categoryAxis.axisTick as Record<string, unknown>), show: false };
        categoryAxis.name = presentation.hideYAxisName ? '' : categoryAxis.name;
      }
    } else {
      if (presentation?.hideXAxisLabels) {
        categoryAxis.axisLabel = { ...(categoryAxis.axisLabel as Record<string, unknown>), show: false };
        categoryAxis.axisTick = { ...(categoryAxis.axisTick as Record<string, unknown>), show: false };
        categoryAxis.name = presentation.hideXAxisName ? '' : categoryAxis.name;
      }
      if (presentation?.hideYAxisLabels) {
        valueAxis.axisLabel = { ...(valueAxis.axisLabel as Record<string, unknown>), show: false };
        valueAxis.axisTick = { ...(valueAxis.axisTick as Record<string, unknown>), show: false };
        valueAxis.name = presentation.hideYAxisName ? '' : valueAxis.name;
      }
    }

    if (isDiverging) {
      valueAxis.axisLine = { show: true, lineStyle: { color: '#D9CFC4' } };
    }

    return {
      ...base,
      barCategoryGap: '22%',
      barGap: multiBar ? '35%' : '20%',
      ...(isHorizontal
        ? { xAxis: valueAxis, yAxis: { ...categoryAxis, type: 'category' as const } }
        : { xAxis: categoryAxis, yAxis: valueAxis }
      ),
      series,
      tooltip: {
        ...(base.tooltip as object),
        trigger: 'axis',
        formatter: (params: unknown) => {
          const items = Array.isArray(params) ? params : [params];
          const label = (items[0] as { name?: string })?.name ?? '';
          let html = `<div style="margin-bottom:6px;font-size:${CHART_LABEL_FONT_SIZE}px;color:#F7F3EE">${label}</div>`;
          for (const item of items) {
            const i = item as { marker?: string; seriesName?: string; value?: number };
            html += `<div style="display:flex;align-items:center;gap:8px;font-size:${CHART_LABEL_FONT_SIZE}px">${i.marker}<span>${i.seriesName}</span><span style="margin-left:auto">${formatTooltipValue(i.value)}</span></div>`;
          }
          const stats = getChartValueStats(data, fields);
          html += `<div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(247,243,238,.22);font-size:11px;color:#D9CFC4">${formatTooltipStatsLine(stats)}</div>`;
          return html;
        },
      },
      // Range slider intentionally not rendered for now.
      // dataZoom: showRangeSlider ? [
      //   {
      //     type: 'inside',
      //     xAxisIndex: 0,
      //     filterMode: 'none',
      //   },
      //   {
      //     type: 'slider',
      //     xAxisIndex: 0,
      //     filterMode: 'none',
      //     bottom: 20,
      //     height: 20,
      //     borderColor: '#EDE6E1',
      //     fillerColor: 'rgba(141, 49, 101, 0.16)',
      //     backgroundColor: 'rgba(148, 163, 184, 0.12)',
      //     handleSize: '85%',
      //     moveHandleSize: 8,
      //     start: data.length > 24 ? 0 : 0,
      //     end: data.length > 24 ? 45 : 100,
      //   },
      // ] : undefined,
      legend: fields.length > 1 ? base.legend : { show: false },
    };
  }, [chart, colors, presentation]);

  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate opts={ECHARTS_OPTS} />;
};
